import { VELOCITY_EPSILON } from './constants'
import { cellKey, parsePort, portKey } from './keys'
import type { Edge, Locomotive, Network, Vehicle } from './types'

export function getEdge(network: Network, edgeId: string): Edge | undefined {
  return network.edges.get(edgeId)
}

export function getVehicleById(network: Network, id: string): Vehicle | undefined {
  if (network.locomotive?.id === id) return network.locomotive
  if (network.npcs.has(id)) return network.npcs.get(id)
  return network.wagons.get(id)
}

export function isLocomotiveMoving(loco: Locomotive | undefined): boolean {
  return !!loco && Math.abs(loco.currentVelocity) > VELOCITY_EPSILON
}

const connectionChainCache = new WeakMap<
  Network,
  Map<string, { version: number; parents: ReadonlyMap<string, string> }>
>()

/**
 * BFS through coupling links from `startId`, mapping each reachable vehicle to its parent.
 * Memoized per network until the next link change (`network.linkVersion`), since UI
 * components query it every frame.
 */
export function findConnectionChain(
  network: Network,
  startId: string,
): ReadonlyMap<string, string> {
  let byStart = connectionChainCache.get(network)
  if (!byStart) {
    byStart = new Map()
    connectionChainCache.set(network, byStart)
  }

  const cached = byStart.get(startId)
  if (cached && cached.version === network.linkVersion) return cached.parents

  const parents = new Map<string, string>()
  const queue = [startId]
  const visited = new Set([startId])

  for (let i = 0; i < queue.length; i++) {
    const currentId = queue[i]
    const current = getVehicleById(network, currentId)
    if (!current) continue

    for (const nextId of [current.linkedFront, current.linkedBack]) {
      if (!nextId || visited.has(nextId)) continue

      visited.add(nextId)
      parents.set(nextId, currentId)
      queue.push(nextId)
    }
  }

  byStart.set(startId, { version: network.linkVersion, parents })
  return parents
}

export function fillAllVehicles(network: Network, output: Vehicle[]): Vehicle[] {
  output.length = 0

  if (network.locomotive) output.push(network.locomotive)

  for (const wagon of network.wagons.values()) {
    if (!wagon.id.startsWith('NPC_')) output.push(wagon)
  }

  return output
}

export function getAllVehicles(network: Network): Vehicle[] {
  return fillAllVehicles(network, [])
}

export function fillStandaloneWagons(network: Network, output: Vehicle[]): Vehicle[] {
  output.length = 0

  for (const wagon of network.wagons.values()) {
    if (!wagon.linkedFront && !wagon.linkedBack) output.push(wagon)
  }

  return output
}

export function getStandaloneWagons(network: Network): Vehicle[] {
  return fillStandaloneWagons(network, [])
}

export function isCellOccupied(network: Network, x: number, y: number): boolean {
  return network.occupiedCells.has(cellKey(x, y))
}

export function rebuildOccupiedCells(network: Network): void {
  network.occupiedCells.clear()
  if (network.locomotive) addOccupiedCells(network, network.locomotive)
  for (const npc of network.npcs.values()) addOccupiedCells(network, npc)
  for (const wagon of network.wagons.values()) addOccupiedCells(network, wagon)
}

function addOccupiedCells(network: Network, vehicle: Vehicle) {
  const edge = getEdge(network, vehicle.edgeId)
  if (!edge) return
  const keys = network.occupiedCells
  if (edge.type === 'internal') {
    keys.add(edge.id.slice(2, edge.id.indexOf(':', 2)))
  } else {
    keys.add(edge.a.slice(0, edge.a.indexOf(':')))
    keys.add(edge.b.slice(0, edge.b.indexOf(':')))
  }
}

export function getOccupiedCellKeys(network: Network): Set<string> {
  return network.occupiedCells
}

export function isLocoOnExitCell(network: Network, loco: Locomotive | undefined): boolean {
  if (!loco) return false
  if (loco.edgeId.startsWith('ESCAPE:')) return true
  const edge = network.edges.get(loco.edgeId)
  if (!edge) return false
  const portA = parsePort(edge.a)
  const portB = parsePort(edge.b)
  return network.exitCells.some(
    c => (c.x === portA.x && c.y === portA.y) || (c.x === portB.x && c.y === portB.y),
  )
}

export function isConsistComplete(network: Network, goal: string[]): boolean {
  const loco = network.locomotive
  if (!loco) return false
  if (loco.linkedFront) return false

  const order: string[] = []
  const visited = new Set<string>([loco.id])
  let previousId = loco.id
  let currentId = loco.linkedBack

  while (currentId) {
    if (visited.has(currentId)) return false
    visited.add(currentId)

    const wagon = network.wagons.get(currentId)
    if (!wagon) return false

    order.push(wagon.color)

    const links = [wagon.linkedFront, wagon.linkedBack].filter((id): id is string => !!id)
    if (!links.includes(previousId)) return false

    const nextLinks = links.filter(id => id !== previousId)
    if (nextLinks.length > 1) return false

    previousId = currentId
    currentId = nextLinks[0]
  }

  return order.length === goal.length && order.every((c, i) => c === goal[i])
}

export function hasClearPathToExit(network: Network): boolean {
  const loco = network.locomotive
  if (!loco) return false

  if (isLocoOnExitCell(network, loco)) return true

  const consistIds = new Set<string>()
  consistIds.add(loco.id)
  const queueIds = [loco.linkedFront, loco.linkedBack].filter((id): id is string => !!id)

  for (let i = 0; i < queueIds.length; i++) {
    const id = queueIds[i]
    if (consistIds.has(id)) continue
    consistIds.add(id)
    const wagon = network.wagons.get(id)
    if (wagon) {
      for (const link of [wagon.linkedFront, wagon.linkedBack]) {
        if (link && !consistIds.has(link)) queueIds.push(link)
      }
    }
  }

  const blockedEdges = new Set<string>()
  for (const wagon of network.wagons.values()) {
    if (!consistIds.has(wagon.id)) blockedEdges.add(wagon.edgeId)
  }
  for (const npc of network.npcs.values()) {
    if (!consistIds.has(npc.id)) blockedEdges.add(npc.edgeId)
  }

  const edge = network.edges.get(loco.edgeId)
  if (!edge) return false

  const startPorts = [edge.a, edge.b]
  const targetPorts = new Set<string>()
  for (const exitCell of network.exitCells) {
    if (exitCell.exit) {
      targetPorts.add(portKey(exitCell.x, exitCell.y, exitCell.exit))
    }
  }

  const visited = new Set<string>(startPorts)
  const queue: string[] = [...startPorts]

  for (let i = 0; i < queue.length; i++) {
    const current = queue[i]
    if (targetPorts.has(current)) return true

    for (const adj of network.adjacency.get(current) ?? []) {
      if (blockedEdges.has(adj.edgeId)) continue
      if (!visited.has(adj.to)) {
        visited.add(adj.to)
        queue.push(adj.to)
      }
    }
  }

  return false
}
